import React, { createContext, useContext, useState, useEffect } from 'react';
import { getBrowserLanguage, isLanguageAvailable } from '../config/languages';

interface TranslationData {
  [key: string]: any;
}

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (langCode: string) => void;
  t: (key: string, replacements?: { [key: string]: string | number }) => any;
  isLoaded: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const savedLang = localStorage.getItem('selectedLanguage');
    if (savedLang && isLanguageAvailable(savedLang)) {
      return savedLang;
    }
    const browserLang = getBrowserLanguage();
    return isLanguageAvailable(browserLang) ? browserLang : 'it_IT';
  });
  
  const [translations, setTranslations] = useState<TranslationData>(() => {
    // Usa traduzioni precaricate per la lingua iniziale se disponibili
    if (window.__PRELOADED_TRANSLATIONS__ && window.__PRELOADED_TRANSLATIONS__[currentLanguage]) {
      return window.__PRELOADED_TRANSLATIONS__[currentLanguage];
    }
    
    // Fallback: usa window.translations solo se la lingua iniziale è inglese
    if ((window as any).translations && currentLanguage === 'en_GB') {
      return (window as any).translations;
    }
    
    return {};
  });
  
  const [isLoaded, setIsLoaded] = useState(() => {
    // Caricato se abbiamo traduzioni per la lingua corrente
    if (window.__PRELOADED_TRANSLATIONS__ && window.__PRELOADED_TRANSLATIONS__[currentLanguage]) {
      return true;
    }
    // O se window.translations è disponibile e la lingua è inglese
    return !!((window as any).translations && currentLanguage === 'en_GB');
  });

  const loadTranslations = async (langCode: string) => {
    setIsLoaded(false);
    
    // Priorità 1: usa traduzioni precaricate dinamicamente per la lingua specifica
    if (window.__PRELOADED_TRANSLATIONS__ && window.__PRELOADED_TRANSLATIONS__[langCode]) {
      setTranslations(window.__PRELOADED_TRANSLATIONS__[langCode]);
      setIsLoaded(true);
      return;
    }
    
    // Priorità 2: carica dinamicamente da file JSON
    try {
      const fileName = `/${langCode}.json`;
      const response = await fetch(fileName);
      
      if (response.ok) {
        const data = await response.json();
        setTranslations(data);
        // Salva nella cache globale per uso futuro
        if (!window.__PRELOADED_TRANSLATIONS__) {
          window.__PRELOADED_TRANSLATIONS__ = {};
        }
        window.__PRELOADED_TRANSLATIONS__[langCode] = data;
        setIsLoaded(true);
      } else {
        throw new Error('Failed to fetch translations');
      }
    } catch (error) {
      console.error('Error loading translations:', error);
      
      // Priorità 3: Fallback su window.translations solo se è la stessa lingua
      if ((window as any).translations && langCode === 'en_GB') {
        setTranslations((window as any).translations);
        setIsLoaded(true);
        return;
      }
      
      // Fallback to module import
      try {
        let modulePath = '../../it_IT.json'; // default
        if (langCode === 'en_GB') {
          modulePath = '../../en_GB.json';
        } else if (langCode === 'de_DE') {
          modulePath = '../../de_DE.json';
        } else if (langCode === 'fr_FR') {
          modulePath = '../../fr_FR.json';
        }
        const module = await import(modulePath);
        setTranslations(module.default);
        setIsLoaded(true);
      } catch (moduleError) {
        console.error('Error loading translations module:', moduleError);
        // Set minimal fallback
        setTranslations({
          common: { loading: 'Loading...' },
          header: { siteName: 'EscortFun.net' }
        });
        setIsLoaded(true);
      }
    }
  };

  useEffect(() => {
    // Svuota la cache quando cambia la lingua
    translationCache.clear();
    loadTranslations(currentLanguage);
  }, [currentLanguage]);

  // Ascolta evento di traduzioni caricate dinamicamente
  useEffect(() => {
    const handleTranslationsLoaded = (event: CustomEvent) => {
      const loadedLang = event.detail;
      if (loadedLang === currentLanguage && window.__PRELOADED_TRANSLATIONS__[loadedLang]) {
        setTranslations(window.__PRELOADED_TRANSLATIONS__[loadedLang]);
        setIsLoaded(true);
      }
    };

    window.addEventListener('translationsLoaded', handleTranslationsLoaded as EventListener);
    return () => {
      window.removeEventListener('translationsLoaded', handleTranslationsLoaded as EventListener);
    };
  }, [currentLanguage]);

  const setLanguage = (langCode: string) => {
    if (isLanguageAvailable(langCode)) {
      setCurrentLanguage(langCode);
      localStorage.setItem('selectedLanguage', langCode);
    }
  };

  // Cache per le traduzioni per evitare re-parsing
  const translationCache = useState(new Map<string, any>())[0];

  const t = (key: string, replacements?: { [key: string]: string | number }): any => {
    // Usa cache per performance
    const cacheKey = replacements ? `${key}:${JSON.stringify(replacements)}` : key;
    if (translationCache.has(cacheKey)) {
      return translationCache.get(cacheKey);
    }

    // Se non caricate, ritorna la key (ma non bloccare il render)
    if (!isLoaded || Object.keys(translations).length === 0) {
      return key;
    }

    const keys = key.split('.');
    let value: any = translations;

    for (const k of keys) {
      if (value === undefined || value === null) {
        break;
      }
      value = value[k];
    }

    // Solo warning se le traduzioni sono caricate
    if (value === undefined || value === null) {
      if (import.meta.env.DEV) {
        console.warn(`[Translation] MISSING - Language: ${currentLanguage}, Key: ${key}`);
      }
      translationCache.set(cacheKey, key);
      return key;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      translationCache.set(cacheKey, value);
      return value;
    }

    // Convert non-strings to strings
    if (typeof value !== 'string') {
      const result = String(value);
      translationCache.set(cacheKey, result);
      return result;
    }

    // Apply replacements if provided
    if (replacements) {
      let result: string = value;
      for (const [placeholder, replacement] of Object.entries(replacements)) {
        result = result.replace(new RegExp(`{${placeholder}}`, 'g'), String(replacement));
      }
      translationCache.set(cacheKey, result);
      return result;
    }

    translationCache.set(cacheKey, value);
    return value;
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, setLanguage, t, isLoaded }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};